/**
 * Phase 7 · `.sample-answer-blur` gated reveal handler.
 * NEW pattern (not in `01_BUILD/qa_studio.html`) per Genie ack 2 MAY 2026
 * (#2=any markup) — applies to any blur block, not just Mission tickets.
 *
 * Pedagogy: blur the sample answer, force user to click reveal button so they
 * attempt the task before checking. Reveal state is session-only (lost on
 * navigation away from ticket) — re-attempt-then-check on revisit is desired.
 *
 * Markup contract:
 *   <div class="sample-answer-blur" data-blur-id="<unique>">
 *     <div class="sample-content">…the actual answer markup…</div>
 *     <div class="sample-reveal">
 *       <span class="reveal-tag">Sample answer</span>
 *       <p class="reveal-prompt">Сначала попробуй сам/сама.</p>
 *       <button type="button" class="reveal-btn">Показать пример</button>
 *     </div>
 *   </div>
 */
export function initSampleReveal(root: HTMLElement): () => void {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>('.sample-answer-blur'));
  const cleanups: Array<() => void> = [];

  for (const block of blocks) {
    if (block.dataset.blurInit === '1') continue;
    block.dataset.blurInit = '1';

    const overlay = block.querySelector<HTMLElement>('.sample-reveal');
    const button = block.querySelector<HTMLButtonElement>('.reveal-btn');
    if (!overlay || !button) continue;

    button.setAttribute('aria-expanded', block.classList.contains('revealed') ? 'true' : 'false');

    const onReveal = () => {
      block.classList.add('revealed');
      button.setAttribute('aria-expanded', 'true');
    };

    const onOverlayClick = (e: MouseEvent) => {
      // Allow click anywhere on overlay (not just button) to reveal — easier on touch.
      if (e.target === overlay || (e.target as HTMLElement).closest('.sample-reveal') === overlay) {
        onReveal();
      }
    };

    button.addEventListener('click', onReveal);
    overlay.addEventListener('click', onOverlayClick);

    cleanups.push(() => {
      button.removeEventListener('click', onReveal);
      overlay.removeEventListener('click', onOverlayClick);
      delete block.dataset.blurInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
